/**
 * Reports > Top Gifts panel (GFG-272) — the whole tab: the two filter menus, Export CSV, the
 * count band and the ranked table, over `GET /reports/top-gifts`.
 *
 * Not a `Blotter` like its two sibling tabs: that component always renders a search box and a
 * pager, and this report has neither — the filtered set IS the whole set. It borrows the
 * Blotter's count band and scroll box so the three Reports tabs still read as one screen.
 */
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { Alert, Button } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { DatePresetMenu } from "@/components/DatePresetMenu";
import { TopGiftsCampaignMenu } from "@/components/reports/TopGiftsCampaignMenu";
import { TopGiftsMenuButton } from "@/components/reports/TopGiftsMenuButton";
import TopGiftsTable from "@/components/reports/TopGiftsTable";
import { buildTopGiftColumns, buildTopGiftCsvColumns } from "@/components/reports/topGiftsColumns";
import { GfgButton } from "@/components/ui/GfgButton";
import { MIN_WIDTH, TOP_GIFTS_TEXT } from "@/constants/reportsTopGifts";
import { ImpactMenuCoordinatorProvider } from "@/context/ImpactMenuCoordinatorContext";
import { useReportsTopGifts } from "@/hooks/useReportsTopGifts";
import type { TopGiftsFilterOptions, TopGiftsSelection } from "@/interfaces/reportsTopGifts";
import { buildCsv, downloadCsv } from "@/lib/csv";
import { businessToday } from "@/utils/date-format";
import { resolveImpactDateWindow } from "@/utils/impact-report-filter-helper";
import {
  periodButtonLabel,
  topGiftsCountLabel,
  toTopGiftsParams,
} from "@/utils/reportsTopGifts-helper";

/** Both filters default to "everything", which is what makes the empty state escapable. */
const INITIAL_SELECTION: TopGiftsSelection = { campaignIds: [], date: { preset: "allTime" } };

export default function TopGiftsPanel() {
  const [selection, setSelection] = useState<TopGiftsSelection>(INITIAL_SELECTION);
  const [filterOptions, setFilterOptions] = useState<TopGiftsFilterOptions | null>(null);

  // Today in New York, not the browser's: the API reads the window as New York days. Kept INSIDE
  // the memo: the params carry only the civil day, so they are rebuilt per selection, not render.
  const params = useMemo(() => toTopGiftsParams(selection, businessToday()), [selection]);
  const { data, isLoading, isError, refetch } = useReportsTopGifts(params);

  const rows = data?.data ?? [];

  // The campaign list is held in state rather than read off the response: picking a filter moves
  // react-query to a cold cache entry, and the dropdown would empty out mid-selection.
  const responseFilterOptions = data?.filterOptions;
  useEffect(() => {
    if (responseFilterOptions) setFilterOptions(responseFilterOptions);
  }, [responseFilterOptions]);

  // One `now` for the label and the lit state, so the two can never disagree by a midnight.
  const now = businessToday();
  const hasWindow = resolveImpactDateWindow(selection.date, now) !== null;

  function handleExportCsv() {
    // No pagination, so the screen already holds every row the filter matches — re-fetching for
    // the export would only ask the same question twice.
    downloadCsv(TOP_GIFTS_TEXT.csvFilename, buildCsv(buildTopGiftCsvColumns(), rows));
  }

  return (
    <>
      {/* One coordinator around BOTH menus: it is what makes moving from an open menu to the
          other cost one tap instead of two, because MUI's backdrop eats the first click. */}
      <ImpactMenuCoordinatorProvider>
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <TopGiftsCampaignMenu
            selected={selection.campaignIds}
            options={filterOptions?.campaigns ?? []}
            // Only while there is no list yet — otherwise every filter change would replace the
            // campaigns already on screen with the loading line.
            isLoading={filterOptions === null && isLoading}
            onChange={(campaignIds) => setSelection((previous) => ({ ...previous, campaignIds }))}
          />

          <DatePresetMenu
            value={selection.date}
            onChange={(date) => setSelection((previous) => ({ ...previous, date }))}
            renderTrigger={({ onOpen, isOpen, triggerAttributes }) => (
              <TopGiftsMenuButton
                label={
                  hasWindow
                    ? `${periodButtonLabel(selection.date, now)} ${TOP_GIFTS_TEXT.periodZone}`
                    : periodButtonLabel(selection.date, now)
                }
                isActive={hasWindow}
                isOpen={isOpen}
                onOpen={onOpen}
                triggerAttributes={triggerAttributes}
                ariaLabel={TOP_GIFTS_TEXT.periodMenuAria}
              />
            )}
          />

          <GfgButton
            variant="secondary"
            startIcon={<FileDownloadOutlined sx={{ fontSize: 18 }} />}
            disabled={isLoading || rows.length === 0}
            onClick={handleExportCsv}
          >
            {TOP_GIFTS_TEXT.exportCsv}
          </GfgButton>
        </div>
      </ImpactMenuCoordinatorProvider>

      {/* Above the table, never in place of it: replacing it would throw away the very filter the
          user is retrying with. `refetch` rather than the siblings' `invalidateQueries` — this
          panel owns its query instead of handing it to a `Blotter` factory. */}
      {isError && (
        <Alert
          severity="error"
          role="alert"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              {TOP_GIFTS_TEXT.retry}
            </Button>
          }
        >
          {TOP_GIFTS_TEXT.listError}
        </Alert>
      )}

      <div className="border border-b-0 border-card-border rounded-t-card bg-white px-4 py-2.5 text-[13px] text-graphite">
        {/* Only render once `data` exists — a "0 gifts · 0 redemptions" flash on first load (and
            during the error state, alongside the error banner) would be a misleading count. */}
        {data ? topGiftsCountLabel(data.totalGifts, data.totalRedemptions) : null}
      </div>

      {/* The scroll box the table's sticky header and `minWidth` need — Blotter.tsx:501. */}
      <div className="border border-card-border rounded-b-card bg-white overflow-auto max-h-[calc(100vh-300px)]">
        <TopGiftsTable
          columns={buildTopGiftColumns()}
          rows={rows}
          isLoading={isLoading}
          emptyMessage={TOP_GIFTS_TEXT.emptyMessage}
          minWidth={MIN_WIDTH}
        />
      </div>
    </>
  );
}
